import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useCanvasStores } from '../stores/CanvasContext';
import { isFocusInEditable } from '../../lib/domUtils';

/** Perform undo/redo and provide reactive canUndo/canRedo state. */
export function useUndoRedo() {
  const { txManager } = useCanvasStores();

  const canUndo = useSyncExternalStore(
    (cb) => txManager.history.subscribe(cb),
    () => txManager.history.canUndo(),
  );

  const canRedo = useSyncExternalStore(
    (cb) => txManager.history.subscribe(cb),
    () => txManager.history.canRedo(),
  );

  const undo = useCallback(() => {
    txManager.undo();
  }, [txManager]);

  const redo = useCallback(() => {
    txManager.redo();
  }, [txManager]);

  return { undo, redo, canUndo, canRedo };
}

/** Registers Ctrl+Z / Ctrl+Shift+Z keyboard shortcuts. Call ONCE. */
export function useUndoRedoKeyboard() {
  const { undo, redo } = useUndoRedo();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (isFocusInEditable()) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      if (e.key.toLowerCase() === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          redo();
        } else {
          e.preventDefault();
          undo();
        }
      } else if (e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);
}
